import React, { useState, useMemo, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faChevronLeft, 
  faChevronRight, 
  faCalendarDay, 
  faCalendarWeek
} from '@fortawesome/free-solid-svg-icons';
import { addDays, addMonths, startOfWeek, parseISO, startOfDay, startOfMonth, endOfMonth, eachDayOfInterval } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../store/hooks';
import { SlidingPills } from '../../components/ui/SlidingPills';
import BtnTaratic from '../../components/ui/BtnTaratic';
import type { Appointment, ClinicRoom } from '../../services/clinicsService';
import type { ClinicSchedule, ClinicHoliday } from '../../services/clinicSchedulesService';
import WeeklyResourceGrid from './calendar/WeeklyResourceGrid';

interface ClinicsAgendaCalendarProps {
  rooms: ClinicRoom[];
  appointments: Appointment[];
  schedules: ClinicSchedule[];
  holidays: ClinicHoliday[];
  onSlotClick?: (date: Date, room: ClinicRoom) => void;
  onAppointmentClick?: (appointment: Appointment, event: React.MouseEvent) => void;
  onRoomClick?: (room: ClinicRoom) => void;
  startDate?: string;
  endDate?: string;
}

export default function ClinicsAgendaCalendar({
  rooms,
  appointments,
  schedules,
  holidays,
  onSlotClick,
  onAppointmentClick,
  onRoomClick,
  startDate,
  endDate
}: ClinicsAgendaCalendarProps) {
  const isDocked = useAppSelector((state) => state.chat.isDocked);
  const [currentDate, setCurrentDate] = useState<Date>(startDate ? parseISO(startDate) : new Date());
  const [viewMode, setViewMode] = useState<'day' | 'week' | 'month'>(() => {
    const saved = localStorage.getItem('clinics_calendar_viewMode');
    return (saved as 'day' | 'week' | 'month') || 'week';
  });
  const { t, i18n } = useTranslation();

  const handleViewModeChange = (id: string) => {
    setViewMode(id as 'day' | 'week');
    localStorage.setItem('clinics_calendar_viewMode', id);
  };
  // Update currentDate when startDate changes
  useEffect(() => {
    if (startDate) {
      setCurrentDate(parseISO(startDate));
    }
  }, [startDate]);

  const daysToRender = useMemo(() => {
    let days: Date[] = [];
    if (viewMode === 'day') {
      days = [startOfDay(currentDate)];
    } else if (viewMode === 'month') {
      days = eachDayOfInterval({ start: startOfMonth(currentDate), end: endOfMonth(currentDate) });
    } else {
      const start = startOfWeek(currentDate, { weekStartsOn: 1 });
      days = Array.from({ length: 7 }).map((_, i) => addDays(start, i));
    }

    if (startDate) {
      const s = startOfDay(parseISO(startDate));
      days = days.filter(d => d >= s);
    }
    if (endDate) {
      const e = startOfDay(parseISO(endDate));
      days = days.filter(d => d <= e);
    }
    return days;
  }, [currentDate, viewMode, startDate, endDate]);

  const handlePrev = () => setCurrentDate(prev => {
    if (viewMode === 'month') return addMonths(prev, -1);
    return addDays(prev, viewMode === 'week' ? -7 : -1);
  });
  const handleNext = () => setCurrentDate(prev => {
    if (viewMode === 'month') return addMonths(prev, 1);
    return addDays(prev, viewMode === 'week' ? 7 : 1);
  });

  const formatHeaderDate = () => {
    const locale = i18n.language || 'es';
    if (viewMode === 'week' || viewMode === 'month') {
      return new Intl.DateTimeFormat(locale, {
        month: isDocked ? 'short' : 'long',
        year: 'numeric'
      }).format(viewMode === 'week' ? daysToRender[0] : currentDate);
    }
    return new Intl.DateTimeFormat(locale, {
      weekday: isDocked ? 'short' : 'long',
      day: 'numeric',
      month: isDocked ? 'short' : 'long',
      year: 'numeric'
    }).format(currentDate);
  };

  return (
    <div data-ui="clinics-agenda-calendar-region-001" className="flex flex-col h-full rounded-2xl bg-black/20 backdrop-filter backdrop-blur-xl shadow-inner-glow shadow-inner-glow-black-md drop-shadow-glow-black-md overflow-hidden">
      {/* Header */}
      <div data-ui="clinics-agenda-calendar-region-002" className={`relative flex items-center justify-between border-b border-white/10 ${isDocked ? 'p-3 pr-5 sm:p-4 sm:pr-8' : 'p-3 sm:p-4'}`}>
        <div data-ui="clinics-agenda-calendar-region-003" className="flex-1 flex items-center justify-start min-w-0 pr-2 max-w-[30%] sm:max-w-[38%]">
          <h2 
            className={`font-bold text-white capitalize truncate ${
              isDocked ? 'text-xs sm:text-sm md:text-base' : 'text-base sm:text-xl'
            }`}
            title={formatHeaderDate()}
          >
            {formatHeaderDate()}
          </h2>
        </div>
        
        <div data-ui="clinics-agenda-calendar-region-004" className="absolute left-1/2 -translate-x-1/2">
          <SlidingPills
            options={[
              { id: 'day', label: t('clinics.calendar.day'), icon: faCalendarDay },
              { id: 'week', label: t('clinics.calendar.week'), icon: faCalendarWeek }
            ]}
            activeId={viewMode}
            onChange={handleViewModeChange}
            size="sm"
            variant="dark"
          />
        </div>

        <div data-ui="clinics-agenda-calendar-region-005" className={`flex-1 flex items-center justify-end gap-2 ${isDocked ? 'mr-2 sm:mr-4' : ''}`}>
          <div data-ui="clinics-agenda-calendar-region-006" className="flex gap-1">
            <BtnTaratic color="ghost-dark" iconOnly onClick={handlePrev} aria-label={t('ordersPage.pagination.prev')} title={t('ordersPage.pagination.prev')} className="!w-8 !h-8 rounded-lg">
              <FontAwesomeIcon icon={faChevronLeft} />
            </BtnTaratic>
            <BtnTaratic color="ghost-dark" iconOnly onClick={handleNext} aria-label={t('ordersPage.pagination.next')} title={t('ordersPage.pagination.next')} className="!w-8 !h-8 rounded-lg">
              <FontAwesomeIcon icon={faChevronRight} />
            </BtnTaratic>
          </div>
        </div>
      </div>

      {/* Calendar Grid Wrapper */}
      <div data-ui="clinics-agenda-calendar-region-007" className="flex flex-1 overflow-hidden">
        {viewMode === 'month' ? (
          <div data-ui="clinics-agenda-calendar-region-008" className="flex-1 flex items-center justify-center text-white/50">
            {t('clinics.calendar.noMonthView')}
          </div>
        ) : (
          <WeeklyResourceGrid 
            daysToRender={daysToRender}
            rooms={rooms}
            appointments={appointments}
            schedules={schedules}
            holidays={holidays}
            onSlotClick={onSlotClick}
            onAppointmentClick={onAppointmentClick}
            onRoomClick={onRoomClick}
          />
        )}
      </div>
    </div>
  );
}
